/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path context
 * @description Dynamic year -> model id lookup, built from the actual model list
 * (queried from the backend) instead of a hardcoded table. Models are named
 * "<prefix><year>" (e.g. "TDF2025", "TDF2026"); the year is extracted from the name.
 */
// Query is imported lazily inside getModelByYearMap() rather than at module scope:
// this module is pulled in by AppCtxProvider, and QueryBuilder/QueryAttribute import
// qpr-base/configuration which itself imports AppCtxProvider (useAppContext) — a static
// top-level import here would close that cycle and throw "Cannot access 'System' before
// initialization" on load.

const MODEL_NAME_YEAR_PATTERN = /^([A-Za-z]+)(20\d{2})$/;

let cachedModelByYear: Record<string, string> | null = null;
let inflightFetch: Promise<Record<string, string>> | null = null;

// The MEA query engine returns fully-qualified element ids ("SC.2106935752"), but the rest
// of the app stores/consumes bare model ids and prefixes "SC." itself (see AppCtxProvider's
// fullModelId). Strip it here so a double "SC.SC.<id>" prefix doesn't leak into requests.
const stripModelPrefix = (id: string): string => id.replace(/^SC\./, "");

const buildModelByYearMap = (models: { id: string; name: string }[]): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const model of models) {
    const match = model.name?.match(MODEL_NAME_YEAR_PATTERN);
    if (match) {
      map[match[2]] = stripModelPrefix(model.id);
    }
  }
  return map;
};

/**
 * Fetches (and caches) the year -> model id map from the backend model list.
 * Safe to call multiple times; the underlying query only runs once until refresh() is called.
 */
export const getModelByYearMap = async (): Promise<Record<string, string>> => {
  if (cachedModelByYear) return cachedModelByYear;
  if (!inflightFetch) {
    inflightFetch = import("qpr-base/querybuilder/Query")
      .then(({ default: Query }) =>
        Query("SC").models().attributes("name").fetchValues<{ id: string; name: string }>()
      )
      .then((models) => {
        cachedModelByYear = buildModelByYearMap(models || []);
        inflightFetch = null;
        return cachedModelByYear;
      })
      .catch((e) => {
        inflightFetch = null;
        throw e;
      });
  }
  return inflightFetch;
};

/**
 * Synchronous accessor for code paths that can't await (e.g. inline handlers).
 * Returns an empty map until getModelByYearMap()/warmModelByYearMap() has resolved at least once.
 */
export const getCachedModelByYear = (): Record<string, string> => cachedModelByYear || {};

export const getModelIdForYear = (year: string): string | null => getCachedModelByYear()[year] || null;

/** Kicks off the fetch without waiting on it; call once at app startup. */
export const warmModelByYearMap = (): void => {
  getModelByYearMap().catch(() => {
    // errors are surfaced to callers of getModelByYearMap(); warmup itself stays silent
  });
};

/** Forces the next call to re-fetch from the backend (e.g. after a new model/year is provisioned). */
export const refreshModelByYearMap = (): void => {
  cachedModelByYear = null;
  inflightFetch = null;
};
